import { ChevronDownIcon } from '@heroicons/react/outline';
import {
  SessionQualityScore,
  SessionQualityScoreDimension,
  SessionQualityScoreDimensionKey,
  SessionQualityScoreMoment,
  SessionQualityScoreQuote,
} from '@shared/common';
import { QUALITY_SCORE_GOOD_THRESHOLD, QUALITY_SCORE_POOR_THRESHOLD } from '@shared/constants';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { ReactNode, useState } from 'react';
import QualityScoreBadge from './QualityScoreBadge';

export const DIMENSION_KEYS: SessionQualityScoreDimensionKey[] = ['1', '2', '3', '4', '5', '6'];

export const DIMENSION_SHORT_LABELS: Record<SessionQualityScoreDimensionKey, string> = {
  '1': 'Activation',
  '2': 'Investment',
  '3': 'Curiosity',
  '4': 'Warm+Direct',
  '5': 'Standards',
  '6': 'Near-Peer',
};

export const LOW_SCORE_THRESHOLD = QUALITY_SCORE_POOR_THRESHOLD;

export const scoreTone = (score: number | null | undefined) => {
  if (score === null || score === undefined) return 'text-customGrey';
  if (score >= QUALITY_SCORE_GOOD_THRESHOLD) return 'text-customGreen';
  if (score >= LOW_SCORE_THRESHOLD) return 'text-customGold';
  return 'text-customRed';
};

export const formatScore = (score: number | null | undefined) =>
  score === null || score === undefined ? '—' : score.toFixed(2);

const LEVEL_STYLES: Record<SessionQualityScoreMoment['level'], string> = {
  5: 'bg-customGreen/10 text-customGreen',
  3: 'bg-customGold/10 text-customGold',
  1: 'bg-customRed/10 text-customRed',
};

const LEVEL_LABELS: Record<SessionQualityScoreMoment['level'], string> = {
  5: 'Exemplary',
  3: 'Adequate',
  1: 'Poor',
};

export const QuoteList = (props: { quotes?: SessionQualityScoreQuote[] | null }) => {
  if (!props.quotes?.length) return null;

  return (
    <ul className="flex flex-col gap-1.5">
      {props.quotes.map((quote, index) => (
        <li
          key={`${quote.timestamp}-${index}`}
          className="border-l-2 border-customPrimary/40 pl-3 text-xs italic text-customLight"
        >
          <span className="mr-2 font-mono not-italic text-customGrey">{quote.timestamp}</span>“
          {quote.quote}”
        </li>
      ))}
    </ul>
  );
};

const MomentCard = (props: { moment: SessionQualityScoreMoment }) => {
  const { moment: item } = props;
  const excerpt =
    item.excerpt_start || item.excerpt_end
      ? `${item.excerpt_start ?? '?'} → ${item.excerpt_end ?? '?'}`
      : null;

  return (
    <div className="flex flex-col gap-2 rounded-xl bg-surfaceBackground p-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span
          className={mergeClassNames(
            'rounded-full px-2 py-0.5 font-semibold',
            LEVEL_STYLES[item.level] ?? 'bg-surfaceHover text-customGrey'
          )}
        >
          {LEVEL_LABELS[item.level] ?? `Level ${item.level}`}
        </span>
        <span className="rounded-full bg-surfaceHover px-2 py-0.5 text-customLight">
          {item.scope}
        </span>
        <span className="rounded-full bg-surfaceHover px-2 py-0.5 text-customLight">
          salience {item.salience} · w{item.weight}
        </span>
        {item.confidence ? (
          <span className="rounded-full bg-surfaceHover px-2 py-0.5 text-customLight">
            confidence {item.confidence}
          </span>
        ) : null}
        {item.timestamp ? (
          <span className="ml-auto font-mono text-customGrey">{item.timestamp}</span>
        ) : null}
        {excerpt ? <span className="font-mono text-customGrey">({excerpt})</span> : null}
      </div>
      <p className="text-sm text-white">{item.behaviour}</p>
      <p className="text-xs text-customGrey">{item.rationale}</p>
      {item.recommendation ? (
        <p className="text-xs text-customGold">{item.recommendation}</p>
      ) : null}
      <QuoteList quotes={item.evidence_quotes} />
    </div>
  );
};

export const DimensionPanel = (props: {
  dimensionKey: SessionQualityScoreDimensionKey;
  dimension?: SessionQualityScoreDimension;
  defaultIsOpen?: boolean;
}) => {
  const { dimensionKey, dimension, defaultIsOpen = false } = props;
  const [isOpen, setIsOpen] = useState(defaultIsOpen);
  const momentCount = dimension?.moments?.length ?? 0;

  return (
    <div className="rounded-xl bg-surfaceObject">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <span className="w-5 text-xs text-customGrey">{dimensionKey}</span>
        <span className="flex-1 text-sm font-medium text-white">
          {dimension?.name ?? DIMENSION_SHORT_LABELS[dimensionKey]}
        </span>
        {dimension?.na ? (
          <span className="rounded-full bg-surfaceHover px-2 py-0.5 text-xs text-customGrey">
            n/a
          </span>
        ) : null}
        <span
          className={mergeClassNames('tabular-nums font-semibold', scoreTone(dimension?.score))}
        >
          {dimension?.na ? '—' : formatScore(dimension?.score)}
        </span>
        <span className="text-xs text-customGrey">
          {momentCount} {momentCount === 1 ? 'moment' : 'moments'}
        </span>
        <ChevronDownIcon
          className={mergeClassNames(
            'h-4 w-4 text-customGrey transition-transform',
            isOpen ? 'rotate-180' : ''
          )}
        />
      </button>
      {isOpen ? (
        <div className="flex flex-col gap-2 px-4 pb-4">
          {momentCount ? (
            dimension?.moments.map((item, index) => <MomentCard key={index} moment={item} />)
          ) : (
            <span className="text-xs text-customGrey">No moments recorded.</span>
          )}
        </div>
      ) : null}
    </div>
  );
};

interface QualityScoreDetailsProps {
  qualityScoring: SessionQualityScore;
  headerSlot?: ReactNode;
  showOverallScore?: boolean;
  className?: string;
}

const QualityScoreDetails = (props: QualityScoreDetailsProps) => {
  const { qualityScoring, headerSlot, showOverallScore = false, className } = props;

  const gradedAt = qualityScoring.graded_at
    ? moment(qualityScoring.graded_at).format('D MMM YYYY, HH:mm')
    : 'n/a';

  return (
    <div className={mergeClassNames('flex flex-col gap-5', className)}>
      <div className="flex flex-wrap items-center gap-2">
        {headerSlot}
        {showOverallScore ? (
          <div className="flex items-baseline gap-2 rounded-xl bg-surfaceObject px-4 py-2">
            <span className="text-xs uppercase tracking-wide text-customGrey">Overall</span>
            <span
              className={mergeClassNames(
                'text-2xl font-bold tabular-nums',
                scoreTone(qualityScoring.score)
              )}
            >
              {formatScore(qualityScoring.score)}
            </span>
            <span className="text-xs text-customGrey">/ 5</span>
            <QualityScoreBadge score={qualityScoring.score} className="ml-2 self-center" />
          </div>
        ) : null}
        <span className="ml-auto text-xs text-customGrey">
          Graded {gradedAt} · contract {qualityScoring.grader?.contract ?? '?'} ·{' '}
          {qualityScoring.grader?.model ?? '?'} · effort {qualityScoring.grader?.effort ?? '?'}
          {qualityScoring.grader?.version ? ` · v${qualityScoring.grader.version}` : ''} · schema{' '}
          {qualityScoring.schema} · status {qualityScoring.status}
        </span>
      </div>

      <div className="flex flex-col gap-2 rounded-xl bg-surfaceObject p-4">
        <span className="text-xs font-semibold uppercase tracking-wide text-customGrey">
          Session feedback
        </span>
        <p className="text-sm text-white">{qualityScoring.feedback?.text || '—'}</p>
        <QuoteList quotes={qualityScoring.feedback?.grounded_in} />
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-customGrey">
          Dimensions
        </span>
        {DIMENSION_KEYS.map((key) => (
          <DimensionPanel key={key} dimensionKey={key} dimension={qualityScoring.dims?.[key]} />
        ))}
      </div>
    </div>
  );
};

export default QualityScoreDetails;
